/**
 * Admin Dashboard Page
 * Shows all accounts and system controls (admin session required)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Alert,
  ButtonGroup,
} from 'react-bootstrap';
import {
  getUsers,
  runSchedulerNow,
  deleteUser,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ResponsiveTable from '../components/ResponsiveTable';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const usersData = await getUsers();
      setUsers(usersData);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunScheduler = async () => {
    try {
      setSuccess(null);
      setError(null);
      await runSchedulerNow();
      setSuccess('Scheduler task completed successfully');
      await fetchData();
    } catch (err) {
      setError('Failed to run scheduler: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete account ${userName}?`)) {
      try {
        await deleteUser(userId);
        setSuccess(`Account ${userName} deleted successfully`);
        await fetchData();
      } catch (err) {
        setError('Failed to delete account: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  if (loading) {
    return <LoadingSpinner message="Loading admin dashboard..." />;
  }

  const autoRegisterCount = users.filter((u) => u.auto_register).length;

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2><i className="bi bi-shield-lock me-2"></i>Admin Dashboard</h2>
          <p className="text-muted">Accounts overview and system controls</p>
        </Col>
        <Col className="text-end">
          <Button variant="outline-danger" size="sm" onClick={handleLogout}>
            <i className="bi bi-lock me-1"></i> Logout
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-value text-primary">{users.length}</div>
              <div className="stat-label">Total Accounts</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-value text-success">{autoRegisterCount}</div>
              <div className="stat-label">Auto Register ON</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Control Buttons */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>System Controls</Card.Header>
            <Card.Body>
              <ButtonGroup>
                <Button variant="success" onClick={handleRunScheduler}>
                  <i className="bi bi-clock me-1"></i> Run Scheduler Now
                </Button>
              </ButtonGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Accounts Management */}
      <Row>
        <Col>
          <Card>
            <Card.Header>Accounts</Card.Header>
            <Card.Body>
              <ResponsiveTable
                data={users}
                columns={[
                  {
                    key: 'tenant',
                    label: 'Tenant',
                    render: (value, user) => (
                      <>
                        <strong>{user.tenant}</strong>
                        <br />
                        <small className="text-muted">{user.name}</small>
                        {user.address && (
                          <><br /><small className="text-muted">{user.address}</small></>
                        )}
                      </>
                    ),
                  },
                  {
                    key: 'license_plate',
                    label: 'License Plate',
                    render: (value, user) => user.license_plate || 'N/A',
                  },
                  {
                    key: 'phone_number',
                    label: 'Phone',
                    render: (value, user) => user.phone_number || 'N/A',
                  },
                  {
                    key: 'member_count',
                    label: 'Members',
                    render: (value, user) => (
                      <Badge bg="info">{user.member_count ?? 0}</Badge>
                    ),
                  },
                  {
                    key: 'auto_register',
                    label: 'Auto Register',
                    render: (value, user) => (
                      <Badge bg={user.auto_register ? 'success' : 'secondary'}>
                        {user.auto_register ? 'ON' : 'OFF'}
                      </Badge>
                    ),
                  },
                  {
                    key: 'pref_time',
                    label: 'Auto-register',
                    render: (value, user) => `Daily at ${user.pref_time}`,
                  },
                  {
                    key: 'created_at',
                    label: 'Joined',
                    render: (value, user) => formatDate(user.created_at, 'MMM d, yyyy'),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (value, user) => (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id, user.tenant)}
                      >
                        Delete
                      </Button>
                    ),
                  },
                ]}
                renderRow={(user) => (
                  <>
                    <div className="table-card-primary">{user.tenant}</div>
                    <div className="table-card-secondary">{user.name}</div>
                    <div className="table-card-divider"></div>
                    <div className="table-card-field">
                      <strong>License Plate:</strong> {user.license_plate || 'N/A'}
                    </div>
                    <div className="table-card-field">
                      <strong>Phone:</strong> {user.phone_number || 'N/A'}
                    </div>
                    <div className="table-card-field">
                      <strong>Members:</strong>{' '}
                      <Badge bg="info">{user.member_count ?? 0}</Badge>
                    </div>
                    <div className="table-card-field">
                      <strong>Auto Register:</strong>{' '}
                      <Badge bg={user.auto_register ? 'success' : 'secondary'}>
                        {user.auto_register ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    <div className="table-card-field">
                      <strong>Auto-register:</strong> daily at {user.pref_time}
                    </div>
                    <div className="table-card-field">
                      <strong>Joined:</strong> {formatDate(user.created_at, 'MMM d, yyyy')}
                    </div>
                    <div className="table-card-actions">
                      <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteUser(user.id, user.tenant)}
                      >
                        Delete
                      </Button>
                    </div>
                  </>
                )}
                emptyState={<EmptyState icon="people" message="No accounts yet" />}
                hover={true}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboard;
